import { HOUSEHOLD_DOC, householdMemberSchema, type HouseholdMember } from '@awwyeah/shared';
import { Cake, Copy, Pencil, UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useYDoc, useYMap } from '../../sync';
import { apiCreateInvite } from '../auth/api';
import { ProfileCard, useUser } from '../auth';
import { ProfileEditSheet } from './components/ProfileEditSheet';

/**
 * Minimal household screen (full design is Screen G, Phase 2/WS-10): who's in the
 * household, and the invite-link generator the family joins through. Members appear
 * here as each person logs in for the first time (profiles self-register into the
 * `household` doc — see calendar's useHouseholdData).
 *
 * The page itself still lives at `/household` (deep-links and tests keep working), but as
 * of 2026-07-31 it's no longer a nav destination — `HouseholdContent` below is embedded at
 * the top of Settings instead (docs/DECISIONS.md). This component is now just that content
 * plus the page-level heading.
 */
export function HouseholdPage(): JSX.Element {
  return (
    <div className="mx-auto flex max-w-lg flex-col gap-6 py-6" data-testid="household-page">
      <header>
        <h1 className="font-display text-2xl text-fg">Household</h1>
        <p className="mt-1 text-sm text-fg-2">
          The people this all belongs to. Everything in AwwYeah is shared with exactly these humans
          and no one else.
        </p>
      </header>
      <HouseholdContent />
    </div>
  );
}

/**
 * The reusable guts of the household screen — member roster + invite-link generator —
 * with no page chrome of its own, so it can be embedded (Settings) as well as rendered
 * standalone (`HouseholdPage`, above).
 */
export function HouseholdContent(): JSX.Element {
  const { user } = useUser();
  const doc = useYDoc(HOUSEHOLD_DOC);
  const { entries } = useYMap<HouseholdMember>(doc, 'members');
  const [editingProfile, setEditingProfile] = useState(false);

  const members = useMemo(
    () =>
      entries
        .map(([, value]) => householdMemberSchema.safeParse(value))
        .flatMap((p) => (p.success ? [p.data] : []))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [entries],
  );

  return (
    <div className="flex flex-col gap-6">
      {user && (
        <ProfileCard
          user={user}
          action={
            <button
              type="button"
              onClick={() => setEditingProfile(true)}
              className="flex min-h-[44px] items-center gap-1.5 rounded-md border border-line px-3 text-sm font-semibold text-fg-2"
              data-testid="household-edit-profile"
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
              Edit
            </button>
          }
        />
      )}

      <section className="rounded-md border border-line bg-surface" data-testid="household-members">
        <div className="flex h-10 items-center border-b border-line px-4">
          <span className="text-xs font-semibold uppercase tracking-wider text-fg-3">
            Members · {members.length}
          </span>
        </div>
        {members.map((m) => {
          const isMe = user?.id === m.id;
          return (
            <div
              key={m.id}
              className="flex min-h-[56px] items-center gap-3 border-b border-line px-4 last:border-b-0"
              data-testid="household-member"
            >
              <span
                className="flex h-9 w-9 items-center justify-center rounded-full text-lg"
                style={{ backgroundColor: m.color }}
                aria-hidden="true"
              >
                {m.avatarEmoji ?? m.name.charAt(0).toUpperCase()}
              </span>
              <span className="flex-1 text-base font-semibold text-fg">
                {m.name}
                {isMe && <span className="ml-2 text-xs font-medium text-fg-3">you</span>}
              </span>
            </div>
          );
        })}
        {members.length === 0 && (
          <p className="px-4 py-6 text-sm text-fg-3">
            No profiles yet — they appear as each person logs in for the first time.
          </p>
        )}
      </section>

      <ProfileEditSheet open={editingProfile} onClose={() => setEditingProfile(false)} />

      <InviteSection />

      <section className="flex items-center gap-3 rounded-md border border-dashed border-line px-4 py-3 text-sm text-fg-3">
        <Cake className="h-4 w-4 shrink-0" aria-hidden="true" />
        Birthdays live on the calendar for now — a fuller household screen (profiles, birthdays,
        preferences) is on the roadmap.
      </section>
    </div>
  );
}

function InviteSection(): JSX.Element {
  const [state, setState] = useState<
    | { phase: 'idle' }
    | { phase: 'busy' }
    | { phase: 'ready'; url: string; expiresAt: string; copied: boolean }
    | { phase: 'error'; message: string }
  >({ phase: 'idle' });

  const createInvite = async (): Promise<void> => {
    setState({ phase: 'busy' });
    try {
      const { token, expiresAt } = await apiCreateInvite();
      const url = `${window.location.origin}/invite/${token}`;
      setState({ phase: 'ready', url, expiresAt, copied: false });
    } catch (err) {
      setState({ phase: 'error', message: err instanceof Error ? err.message : 'Failed' });
    }
  };

  const copy = async (url: string, expiresAt: string): Promise<void> => {
    await navigator.clipboard.writeText(url);
    setState({ phase: 'ready', url, expiresAt, copied: true });
  };

  return (
    <section
      className="flex flex-col gap-3 rounded-md border border-line bg-surface p-4"
      data-testid="household-invite"
    >
      <div className="flex items-center gap-2">
        <UserPlus className="h-4 w-4 text-fg-2" aria-hidden="true" />
        <h2 className="text-sm font-semibold text-fg">Invite someone</h2>
      </div>
      <p className="text-sm text-fg-2">
        Make a one-time link and send it however you like. They pick their own name, color, and
        emoji when they join.
      </p>
      {state.phase === 'ready' ? (
        <div className="flex flex-col gap-2">
          <code
            className="overflow-x-auto whitespace-nowrap rounded border border-line bg-bg px-3 py-2 font-mono text-xs text-fg"
            data-testid="invite-url"
          >
            {state.url}
          </code>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void copy(state.url, state.expiresAt)}
              className="flex min-h-[44px] items-center gap-2 rounded-md bg-inverse px-4 font-semibold text-inverse-fg"
              data-testid="invite-copy"
            >
              <Copy className="h-4 w-4" aria-hidden="true" />
              {state.copied ? 'Copied!' : 'Copy link'}
            </button>
            <span className="text-xs text-fg-3">
              Works once · expires {new Date(state.expiresAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => void createInvite()}
          disabled={state.phase === 'busy'}
          className="min-h-[44px] self-start rounded-md bg-inverse px-4 font-semibold text-inverse-fg disabled:opacity-60"
          data-testid="invite-create"
        >
          {state.phase === 'busy' ? 'Making link…' : 'Make an invite link'}
        </button>
      )}
      {state.phase === 'error' && <p className="text-sm text-danger">{state.message}</p>}
    </section>
  );
}
